import React, { useMemo } from "react";

import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import StatsAPI from "@/src/api/StatsAPI";
import useAbility from "@/src/hooks/useAbility";
import usePeriodFilter from "@/src/hooks/usePeriodFilter";

import PurchasesTable from "./PurchasesTable";
import SubscribersTable from "./SubscribersTable";
import { userWatchedGraphQuery } from "./userActivity.queries";
import {
  buildPlaytimeTimeline,
  buildWatchedMediaTimeline,
} from "./userActivity.utils";
import UserKpiGrid from "./UserKpiGrid";
import UserLastWatched from "./UserLastWatched";
import UserSessions from "./UserSessions";
import ViewsTable from "./ViewsTable";
import AnalyticsPeriodPicker from "../charts/AnalyticsPeriodPicker";
import ExportCsvButton from "../charts/ExportCsvButton";
import TimelineQueryCard from "../charts/TimelineQueryCard";

import type { Moment } from "moment";
import type { JSX } from "react";

import type { AppState } from "@/src/reducers";

import type { PlaytimeKey, WatchedMediaKey } from "./userActivity.utils";
import type { ChartSeries } from "../charts/charts.types";

interface UserActivityTabProps {
  userUuid: string;
  userCreationDate: Moment | null;
}

function UserActivityTab({
  userUuid,
  userCreationDate,
}: UserActivityTabProps): JSX.Element {
  const { t } = useTranslation("editor-analytics");
  const { projectSlug } = useParams<{ projectSlug: string }>();
  const orgaConfig = useSelector(
    (state: AppState) => state.fetchReducer.orga_config,
  );
  const { canAccess } = useAbility();
  const { project: canAccessProject } = canAccess(
    orgaConfig.data?.organization_options,
  );
  const { period, setPeriod } = usePeriodFilter();

  const watchedMediaSeriesList = useMemo(
    (): ChartSeries<WatchedMediaKey>[] => [
      {
        key: "watchedMedia",
        label: t("userActivity.series.watchedMedia"),
        color: "var(--chart-1)",
      },
    ],
    [t],
  );
  const playtimeSeriesList = useMemo(
    (): ChartSeries<PlaytimeKey>[] => [
      {
        key: "playtime",
        label: t("userActivity.series.playtime"),
        color: "var(--chart-2)",
      },
    ],
    [t],
  );

  const fetchCsv = async (): Promise<BlobPart> =>
    StatsAPI.getUserWatchedGraph({
      userId: userUuid,
      periodStart: period.startDate,
      periodEnd: period.endDate,
      isExport: true,
      projectSlug,
    });

  const query = userWatchedGraphQuery(projectSlug, userUuid, period);

  return (
    <div className="@container flex flex-col gap-6">
      {canAccessProject.contentsAnalytics ? (
        <div className="flex justify-end">
          <AnalyticsPeriodPicker
            minDate={userCreationDate}
            onChange={setPeriod}
            period={period}
          />
        </div>
      ) : null}
      <UserKpiGrid userUuid={userUuid} />
      <UserLastWatched userId={userUuid} />
      <UserSessions userId={userUuid} />
      {canAccessProject.offersAnalytics ? (
        <SubscribersTable
          offerAbility={canAccessProject.offersWrite}
          userUuid={userUuid}
        />
      ) : null}
      {canAccessProject.transactionsAnalytics ? (
        <PurchasesTable
          offerAbility={canAccessProject.offersWrite}
          userUuid={userUuid}
        />
      ) : null}
      {canAccessProject.contentsAnalytics ? (
        <React.Fragment>
          <div className="grid gap-4 @3xl:grid-cols-2">
            <TimelineQueryCard
              buildTimeline={buildWatchedMediaTimeline}
              endDate={period.endDate}
              query={query}
              seriesList={watchedMediaSeriesList}
              startDate={period.startDate}
              title={t("userActivity.charts.watchedMedia")}
              extraActions={
                <ExportCsvButton
                  fetchCsv={fetchCsv}
                  fileName={`watched-medias-${userUuid}.csv`}
                />
              }
            />
            <TimelineQueryCard
              buildTimeline={buildPlaytimeTimeline}
              endDate={period.endDate}
              query={query}
              seriesList={playtimeSeriesList}
              startDate={period.startDate}
              title={t("userActivity.charts.playtime")}
              unit="duration"
            />
          </div>
          <ViewsTable
            contentAbility={canAccessProject.contentManagement}
            period={period}
            userUuid={userUuid}
          />
        </React.Fragment>
      ) : null}
    </div>
  );
}

export default UserActivityTab;
